import { Box, Button, Card, Divider, Grid, Group, PasswordInput, Text } from '@mantine/core';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import { setKlaviyoAccount as setKlaviyoAccountRedux } from '../../store/klaviyoSlice';
import ErrorMessage from '../ErrorMessage';

const KlaviyoConnectButton = () => {
  const fetch = useFetch();
  const dispatch = useDispatch();
  const { accountId, domainId } = useParams();
  // OAuth is only offered once the backend carries the Releva-side Klaviyo OAuth app
  // credentials; until then the private API key is the (only) connect method.
  const oauthConfigured = useSelector((state) => state.klaviyo.oauthConfigured);
  const [error, setError] = useState();
  const [connecting, setConnecting] = useState(false);
  const [apiKey, setApiKey] = useState('');

  // OAuth connect: the backend mints state + PKCE and returns Klaviyo's consent URL;
  // after approval Klaviyo redirects to the backend callback, which sends the browser
  // back to this settings page with ?success or ?error.
  const connectWithOAuth = async () => {
    setError(undefined);
    setConnecting(true);
    try {
      const { url } = await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/klaviyo/oauth/url`);
      window.location.assign(url);
    } catch (error) {
      setError(error);
      setConnecting(false);
    }
  };

  // Fallback: a restricted private API key (scopes: events:read, metrics:read, profiles:read).
  const connectWithApiKey = async () => {
    const trimmedApiKey = apiKey.trim();
    if (!trimmedApiKey) {
      return;
    }
    setError(undefined);
    setConnecting(true);
    try {
      const { klaviyoAccount } = await fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/klaviyo/account`, {
        method: 'POST',
        body: JSON.stringify({ apiKey: trimmedApiKey }),
      });
      dispatch(setKlaviyoAccountRedux(klaviyoAccount));
    } catch (error) {
      setError(error);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="sm">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Connection
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            {oauthConfigured && (
              <>
                <Box mt="sm">
                  <Button variant="filled" onClick={connectWithOAuth} loading={connecting}>
                    Connect Klaviyo to Releva
                  </Button>
                </Box>

                <Divider my="md" label="or connect with a private API key" labelPosition="left" />
              </>
            )}

            <Group align="flex-end" gap="xs" mt={oauthConfigured ? 0 : 'sm'}>
              <PasswordInput
                label="Private API key"
                description="Restricted key with events:read/write, metrics:read, profiles:read, flows:read/write, templates:read/write and accounts:read scopes"
                placeholder="pk_..."
                value={apiKey}
                onChange={(event) => setApiKey(event.currentTarget.value)}
                style={{ flexGrow: 1 }}
              />
              <Button
                variant={oauthConfigured ? 'default' : 'filled'}
                onClick={connectWithApiKey}
                loading={connecting}
                disabled={!apiKey.trim()}
              >
                Connect
              </Button>
            </Group>

            {error && <ErrorMessage message={getErrorMessage(error)} />}
          </Grid.Col>
        </Grid>
      </Card.Section>
    </Card>
  );
};

export default KlaviyoConnectButton;
